import React from 'react'
import { Card, Typography, Button } from 'antd'
import { ArrowRightOutlined, GiftOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { dashboardTracker } from '../services/userDashboardTracking'

const { Title, Text } = Typography

interface Holiday {
  id?: string
  name: string
  date: string
  isHalfDay?: boolean
}

interface UpcomingHolidaysProps {
  holidays: Holiday[]
  className?: string
}

const UpcomingHolidaysComponent: React.FC<UpcomingHolidaysProps> = ({
  holidays,
  className = '',
}) => {
  const { formatMessage } = useIntl()

  // Show next 3 holidays to match card heights
  const upcomingHolidays = holidays
    .filter(holiday => dayjs(holiday.date).isAfter(dayjs()))
    .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
    .slice(0, 3)

  return (
    <Card 
      className={`upcoming-holidays ${className}`}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <GiftOutlined style={{ marginRight: 8 }} />
            <Title level={4} style={{ margin: 0 }}>
              {formatMessage({ id: 'dashboard.upcomingHolidays' })}
            </Title>
          </div>
          <Button 
            type="link" 
            href="/app/holidays"
            size="small"
            onClick={() => {
              dashboardTracker.trackActionTaken({
                actionType: 'link_click',
                actionName: 'see_all_holidays',
                elementType: 'link',
                section: 'secondary_grid',
                currentLeaveType: 'unknown',
                currentBalance: 0,
                ledToNavigation: true,
                destinationUrl: '/app/holidays',
              })
            }}
          >
            {formatMessage({ id: 'dashboard.seeAll' })} <ArrowRightOutlined />
          </Button>
        </div>
      }
    >
      {upcomingHolidays.length === 0 ? (
        <div className="no-holidays">
          <Text type="secondary">
            {formatMessage({ id: 'dashboard.noUpcomingHolidays' })}
          </Text>
        </div>
      ) : (
        <div className="holidays-container">
          {upcomingHolidays.map((holiday, index) => {
            const daysTilCount = dayjs(holiday.date).startOf('day').diff(dayjs().startOf('day'), 'day')
            
            return (
              <div 
                key={`${holiday.name}-${holiday.date}`}
                className={`holiday-row ${index === upcomingHolidays.length - 1 ? 'last' : ''}`}
              >
                <span className="holiday-name">
                  {holiday.name}
                </span>
                <span className="holiday-date">
                  {dayjs(holiday.date).format('dddd, MMM DD')} ({formatMessage({ id: 'holidays.inNDays' }, { days: daysTilCount })})
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when holidays data changes
const UpcomingHolidays = React.memo(UpcomingHolidaysComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  return (
    // Check array reference - assumes immutable updates
    prevProps.holidays === nextProps.holidays &&
    prevProps.className === nextProps.className
  )
})

// Set display name for debugging
UpcomingHolidays.displayName = 'UpcomingHolidays'

export default UpcomingHolidays